/**
 * The navigation model, as data.
 *
 * S-D3 approved a sidebar with two modules and eight-ish sections each; almost
 * none of those sections has a screen yet, and the sessions that build them are
 * named beside each entry below. Two ways to handle that were available and
 * only one is honest:
 *
 *  * **Hide what does not exist.** The sidebar then grows silently over a dozen
 *    sessions and is never laid out at its real size until the last one lands —
 *    and the approved information architecture is invisible to anyone reviewing
 *    the shell.
 *  * **Render it disabled.** The model is visible and testable at full size,
 *    and "not available yet" is a state, announced in text (`aria-disabled`
 *    plus a visible label) rather than by colour.
 *
 * The second. What makes it not a placeholder is that there is nothing left to
 * wire up here: the session that builds a module adds its route and flips
 * `to`, and this file is the only edit the shell needs.
 */

/** Which count `GET /api/v1/nav/counts` answers for a section (SHELL-1). */
export type NavCountKey = 'sites' | 'keywords' | 'audit_findings' | 'content' | 'approvals'

export type ModuleId = 'outreach' | 'seo'

export interface NavSection {
  label: string
  /** Absent while the module that owns this section has not shipped. */
  to?: string
  /**
   * SHELL-3. The prefix this entry is *current* for, when that is wider than
   * `to`. Settings is the case that needs it: the entry links to
   * `/settings/organisation` because a settings screen has to open on some
   * panel, and matching on that alone left the rail with nothing highlighted on
   * the other eleven — a sidebar quietly claiming you are nowhere. Since
   * 2026-09-16 the rail is not drawn on `/settings/*` at all (`SettingsShell`),
   * so the entry is never lit; `match` stays because the rule is general and
   * the mobile bar's `isCurrent` reads it for the same reason.
   */
  match?: string
  /** The session that brings it — for the reader of this file, not the user. */
  arrivesIn: string
  /**
   * SHELL-26. Which page in `app/tenancy/pages.Page` this entry is, so the rail
   * lists only what the reader's role can open.
   *
   * **Absent means always shown**, and *Settings* is the case that needs it: the
   * entry links to `/settings/organisation` but the settings screen is a dozen
   * panels behind several different grants, and `SettingsLayout` already filters
   * them one by one. Hiding the whole entry on one panel's grant would take away
   * the eleven the reader can open.
   */
  page?: string
  /**
   * SHELL-1. Which live number `shell.html:69-72` prints after the label. A
   * section with no count key prints none, and a count of zero prints none
   * either — see `SectionNav`.
   */
  count?: NavCountKey
  /**
   * SHELL-2. Draw the count as a warning badge rather than as a quiet figure.
   * Approvals only, and the design's own note says why: it is *"the one thing a
   * tenant opens their phone for"*.
   */
  badge?: boolean
  /**
   * SHELL-6. On the bottom bar below 1100px. Three per module, because the
   * fourth slot is *More* — see `MobileBar`.
   */
  mobile?: boolean
}

export interface ModuleDefinition {
  id: ModuleId
  label: string
  /** Where the switcher lands when this module is chosen. */
  home: string
  sections: NavSection[]
}

export const MODULES: readonly ModuleDefinition[] = [
  {
    id: 'outreach',
    label: 'Outreach',
    home: '/outreach',
    sections: [
      {
        label: 'Overview',
        to: '/outreach',
        arrivesIn: 'S-B8',
        mobile: true,
        page: 'outreach.overview',
      },
      // S-B20. The libraries behind the leads list have existed since S-B13 and
      // no screen read them; this is the session that gave them one, which is
      // the single edit this file's own docstring says a module's session makes.
      {
        label: 'Leads',
        to: '/outreach/leads',
        arrivesIn: 'S-B13',
        mobile: true,
        page: 'outreach.leads',
      },
      {
        label: 'Inbox',
        to: '/outreach/inbox',
        arrivesIn: 'S-B19',
        mobile: true,
        page: 'outreach.inbox',
      },
      {
        label: 'Pipeline',
        to: '/outreach/pipeline',
        arrivesIn: 'S-B20',
        page: 'outreach.pipeline',
      },
      // S-B21. `outreach#packs` — the last disabled entry in either module, and
      // this file's own docstring says flipping it is the single edit a module's
      // session makes here. **The label stays *Niche packs*** even though the
      // screen's h1 is *What we search for*: S-D3 approved seven Outreach
      // sections by name and renaming one is not this session's call.
      { label: 'Niche packs', to: '/outreach/packs', arrivesIn: 'S-B21', page: 'outreach.packs' },
      {
        label: 'Suppression',
        to: '/outreach/suppression',
        arrivesIn: 'S-B12',
        page: 'outreach.suppression',
      },
      {
        label: 'Settings',
        to: '/settings/organisation',
        match: '/settings',
        arrivesIn: 'S-B8',
      },
    ],
  },
  {
    id: 'seo',
    label: 'SEO',
    // `/dashboard` since 2026-09-13 (spec: *Landing*); `/seo` redirects to it.
    home: '/dashboard',
    sections: [
      {
        label: 'Overview',
        to: '/dashboard',
        arrivesIn: 'S-B8',
        mobile: true,
        page: 'seo.overview',
      },
      // S-G7. `seo#sites` — the site list and the per-site uptime monitor.
      { label: 'Sites', to: '/seo/sites', arrivesIn: 'S-B30', count: 'sites', page: 'seo.sites' },
      {
        label: 'Keywords',
        to: '/seo/keywords',
        arrivesIn: 'S-B24',
        count: 'keywords',
        mobile: true,
        page: 'seo.keywords',
      },
      {
        label: 'Technical audit',
        to: '/seo/audit',
        arrivesIn: 'S-B23',
        count: 'audit_findings',
        page: 'seo.audit',
      },
      {
        label: 'Content',
        to: '/seo/content',
        arrivesIn: 'S-B25',
        count: 'content',
        page: 'seo.content',
      },
      {
        label: 'Approvals',
        to: '/seo/approvals',
        arrivesIn: 'S-B27',
        count: 'approvals',
        badge: true,
        mobile: true,
        page: 'seo.approvals',
      },
      // S-B32c. The viewer over S-B29's persisted trail.
      { label: 'Reasoning trail', to: '/seo/trail', arrivesIn: 'S-B32c', page: 'seo.trail' },
      {
        label: 'Settings',
        to: '/settings/organisation',
        match: '/settings',
        arrivesIn: 'S-B8',
      },
    ],
  },
]

/**
 * Screens inside a module that the sidebar deliberately does not list.
 *
 * S-G5, and the rule it set: S-D3 approved eight SEO sections and seven Outreach
 * ones, and *"adding a ninth would be inventing information architecture"* —
 * the call `panels.ts` made when it folded the webhook endpoints into
 * Notifications rather than adding a settings panel nobody drew.
 *
 * But *unlisted* is not *nameless*: without this the breadcrumb fell back to
 * **Overview** on a screen that is not the overview, which is the shell telling
 * a small lie about where you are. So the navigation model keeps the label and
 * `sectionForPath` reads both tables — the sidebar renders `MODULES` only, and
 * still shows exactly the sections that were approved.
 *
 * **SF-18 moved two SEO screens here** (SHELL-13). *Search performance* and
 * *Run triggers* were added to the rail by the sessions that built them, taking
 * it to ten entries against the design's eight — the rule above, broken by the
 * two sessions that came after the session that wrote it. Neither becomes
 * unreachable: *Search performance* is linked from three places on the SEO
 * dashboard and from both Google cards in Settings → Connections, and *Run
 * triggers* from the reasoning trail and from each site's row on `/seo/sites`.
 */
export const SUBSCREENS: readonly NavSection[] = [
  { label: 'Where we publish', to: '/seo/publish-targets', arrivesIn: 'S-G5' },
  // `seo.html#gating` was here as `/seo/approvals/gating` from parity P-GATE-01
  // (2026-09-13) until the settings consolidation of 2026-09-15 moved it to
  // `/settings/approvals`, which the Settings layout titles itself.
  // `seo.html#cannib`. The crumb ended at *Keywords* under a page headed
  // *Keyword cannibalization* (parity P-CAN-02); this entry names the leaf.
  { label: 'Cannibalization', to: '/seo/keywords/cannibalization', arrivesIn: 'S-B24' },
  // S-B22a. The property picker and the figures it produces. Named *Search
  // performance* rather than *Sites* because that is what the screen is about.
  { label: 'Search performance', to: '/seo/search-performance', arrivesIn: 'S-B22a' },
  // S-B29. `seo#triggers` — when AugRank runs, and the unattended conditions
  // that may start a pass. Per site, which is why `/seo/sites` is where it is
  // reached from now that it is not a section of its own.
  { label: 'Run triggers', to: '/seo/triggers', arrivesIn: 'S-B29' },
  // S-B20. `outreach.html#compose`. The same call for the same reason: S-D3
  // approved seven Outreach sections and *What we say* is not one of them. It is
  // reached from the Leads header and from a lead's own score panel, which is
  // where a tenant is when they start wondering what the message says.
  //
  // **A lead's own page gets no entry here**, deliberately. `/outreach/leads/:id`
  // already matches the sidebar's *Leads* by prefix, so the breadcrumb is right
  // without one — and a second entry at the same `to` would make `sectionForPath`
  // pick between two equal-length matches by whichever `filter` happened to see
  // first, which is a breadcrumb decided by array order.
  { label: 'Account health', to: '/outreach/health', arrivesIn: 'S-B31b' },
  { label: 'What we say', to: '/outreach/compose', arrivesIn: 'S-B20' },
  // S-G9. `outreach.html#post`. The same call for the same reason, on the other
  // module: S-D3 approved seven Outreach sections and *Publish to LinkedIn* is
  // not one of them. It is reached from the LinkedIn card in Settings →
  // Connections, which is where a tenant goes when they are thinking about
  // their LinkedIn account rather than about their pipeline.
  { label: 'Publish to LinkedIn', to: '/outreach/post', arrivesIn: 'S-G9' },
]

/** Where the shell remembers which module you were last in. */
export const MODULE_STORAGE_KEY = 'augrank.module'

export function moduleById(id: ModuleId): ModuleDefinition {
  const found = MODULES.find((module) => module.id === id)
  // Total by construction — `ModuleId` is the union of the ids above — but a
  // non-null assertion here would be the one place a typo in that union became
  // a blank sidebar instead of an error.
  if (!found) throw new Error(`No module definition for "${id}"`)
  return found
}

/**
 * Whether a section is the one the given path is on — SHELL-3.
 *
 * `NavLink`'s own matching cannot express this. It compares against `to`, so
 * *Settings* was current on `/settings/organisation` and on nothing else; and
 * the module home needs the opposite treatment, since `/seo` is a prefix of
 * every `/seo/*` route and Overview would otherwise stay lit from inside
 * Keywords. Hence `match` for the first case and an exact test for the second,
 * both decided here rather than in the two components that render a rail.
 */
export function isCurrent(section: NavSection, pathname: string, home: string): boolean {
  if (section.to === undefined) return false
  if (section.to === home) return pathname === home
  const prefix = section.match ?? section.to
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/**
 * The sections the bottom bar carries below 1100px — SHELL-6.
 *
 * Three, because the fourth slot is *More*, which opens the drawer holding
 * everything else (SHELL-4). `shell.html:262-265` names the SEO three —
 * Overview, Keywords, Approvals — and the screen's note says why Approvals is
 * among them. **Outreach's three are this session's judgement**, since S-D3
 * draws only the SEO bar: Overview, Leads and Inbox, which are the three a
 * tenant checks on a phone for the same reason Approvals is on the other bar —
 * something is waiting for them.
 *
 * Falls back to the module's first three if a module ever flags none, so the
 * bar can never render empty.
 */
export function mobileSections(module: ModuleId): NavSection[] {
  const definition = moduleById(module)
  const flagged = definition.sections.filter((section) => section.mobile)
  return flagged.length > 0 ? flagged : definition.sections.slice(0, 3)
}

/**
 * Which module a path belongs to.
 *
 * `/settings` is shared: it is one organisation's settings whichever module you
 * reached it from, so it does not decide the module and the caller's remembered
 * choice stands. Returning null rather than defaulting is what lets the shell
 * keep the switcher where the user left it while they are in settings.
 */
export function moduleForPath(pathname: string): ModuleId | null {
  if (pathname === '/outreach' || pathname.startsWith('/outreach/')) return 'outreach'
  if (pathname === '/seo' || pathname.startsWith('/seo/')) return 'seo'
  // The SEO home moved out from under the `/seo` prefix (spec: *Landing*).
  if (pathname === '/dashboard') return 'seo'
  return null
}

/**
 * The section a path is inside, if the navigation model names one.
 *
 * Added in S-B19, because the Inbox is the **first** section route in the
 * product and the shell's breadcrumb had been hardcoding *Overview* for
 * everything outside `/settings` — correct while every module had one screen,
 * and a lie the moment a second one landed. Derived from the same table the
 * sidebar renders, so a section that gets a route gets a breadcrumb with no
 * second edit.
 *
 * The **longest** match wins: `/outreach` is a prefix of `/outreach/inbox`, and
 * matching the shorter one first would put every future section under Overview
 * again. `null` for a path the model does not name — `/billing/*` is inside the
 * shell and is not a module section, and the caller decides what to say about
 * that rather than this file guessing.
 */
export function sectionForPath(pathname: string): NavSection | null {
  const matches = [...MODULES.flatMap((module) => module.sections), ...SUBSCREENS].filter(
    (section) =>
      section.to !== undefined &&
      (pathname === section.to || pathname.startsWith(`${section.to}/`)),
  )
  return matches.sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0] ?? null
}

/**
 * The remembered module, falling back to SEO.
 *
 * SEO rather than Outreach because it is the module the product leads with and
 * the one a trial starts in (REQUIREMENTS §6). Any stored value that is not a
 * module id is ignored — `localStorage` is user-writable, and a typo there
 * should not be able to produce a shell with no navigation.
 */
export function rememberedModule(storage: Pick<Storage, 'getItem'>): ModuleId {
  const stored = storage.getItem(MODULE_STORAGE_KEY)
  return stored === 'outreach' || stored === 'seo' ? stored : 'seo'
}
